import React from 'react';

import { ApiError } from '@root/features/api-client/api-client';

/**
 * Hook to manage callback handlers with processing state
 * @author filipditrich <ditrich@nfctron.com>
 * @param {T} callback
 * @param {UseHandlerOptions} options
 * @returns {UseHandlerReturnType<T>}
 */
export const useHandler = <T extends (...args: any) => Promise<any> = (...args: any) => Promise<any>, Args extends Parameters<T> = Parameters<T>>(
	callback: T,
	options: UseHandlerOptions = { deps: [] },
): UseHandlerReturnType<T> => {
	const [isProcessing, setIsProcessing] = React.useState(false);
	const [error, setError] = React.useState<ApiError | null>(null);
	const [argsState, setArgsState] = React.useState<Args | null>(null);

	/** main callback handler */
	const callbackHandler = React.useCallback<T>(
		/* @ts-ignore It works as intended, TypeScript just can't comprehend it */

		async (...args: Args) => {
			try {
				setArgsState(args);
				setError(null);
				setIsProcessing(true);
				return await callback(...args);
			} catch (e: ApiError | any) {
				const err = e instanceof ApiError ? e : new ApiError(e.name ?? 'callback_handler_error', e.statusCode ?? undefined, e.message ?? null);
				setError(err);
				options.onError?.(err);
				return err;
			} finally {
				setIsProcessing(false);
			}
		},
		/* eslint-disable-next-line react-hooks/exhaustive-deps */
		[...options.deps, callback],
	);

	return {
		handler: callbackHandler,
		isProcessing,
		error,
		args: argsState,
		cleanUp: () => {
			setIsProcessing(false);
			setError(null);
			setArgsState(null);
		},
	};
};

/**
 * Hook return type
 * @export
 */
export type UseHandlerReturnType<
	T extends (...args: any) => Promise<any> = (...args: any) => Promise<any>,
	Args extends Parameters<T> = Parameters<T>,
	R extends Awaited<PromiseLike<ReturnType<T>>> = Awaited<PromiseLike<ReturnType<T>>>,
> = {
	handler: (...args: Args) => Promise<R | ApiError>;
	isProcessing: boolean;
	error: ApiError | null;
	args: Args | null;
	cleanUp: () => void;
};

export type UseHandlerOptions<T extends (...args: any) => Promise<any> = (...args: any) => Promise<any>> = {
	deps: React.DependencyList;
	onError?: (error: ApiError) => void;
};
